import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BattleField from './battle/BattleField';

// ── Kingdom flag colors ──
const KINGDOM_FLAGS = {
  Italy:   ['#009246', '#ffffff', '#CE2B37'],
  Spain:   ['#c60b1e', '#ffc400', '#c60b1e'],
  France:  ['#002395', '#ffffff', '#ED2939'],
  England: ['#C8102E', '#ffffff', '#C8102E'],
  Unknown: ['#fbbf24', '#ffffff', '#fbbf24'],
};

// ── Waving flag SVG animation ──
const WavingFlag = ({ colors, side, delay = 0, defeated = false }) => {
  const [c1, c2, c3] = defeated ? ['#e5e7eb', '#ffffff', '#e5e7eb'] : colors;
  const flipX = side === 'right' ? -1 : 1;

  return (
    <motion.div
      initial={{ x: side === 'left' ? -200 : 200, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 80, damping: 18, delay }}
      style={{ position: 'absolute', top: '15%', [side]: 0, zIndex: 205, transformOrigin: side }}
    >
      {/* Pole */}
      <div style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: side === 'left' ? 'flex-start' : 'flex-end',
      }}>
        {/* Flag */}
        <motion.div
          animate={{ skewY: [-3, 3, -3], scaleX: [1, 1.04, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay }}
          style={{
            width: 90, height: 60,
            display: 'flex', flexDirection: 'row',
            overflow: 'hidden',
            borderRadius: side === 'left' ? '0 4px 4px 0' : '4px 0 0 4px',
            boxShadow: defeated ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.5)',
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, background: c1 }} />
          <div style={{ flex: 1, background: c2 }} />
          <div style={{ flex: 1, background: c3 }} />
        </motion.div>
        {/* Pole bar */}
        <div style={{
          width: 4, height: '60vh',
          background: defeated ? 'linear-gradient(to bottom, #9ca3af, #6b7280)' : 'linear-gradient(to bottom, #d4af37, #a07d20)',
          [side === 'left' ? 'marginLeft' : 'marginRight']: defeated ? undefined : 0,
          alignSelf: side === 'left' ? 'flex-start' : 'flex-end',
        }} />
      </div>
    </motion.div>
  );
};

// ── Trumpet SVG ──
const Trumpet = ({ side, delay = 0 }) => {
  const flipX = side === 'right' ? -1 : 1;
  return (
    <motion.div
      initial={{ x: side === 'left' ? -300 : 300, y: 40, opacity: 0 }}
      animate={{ x: 0, y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 100, damping: 16, delay }}
      style={{
        position: 'absolute',
        bottom: '22%',
        [side]: 0,
        zIndex: 206,
        transform: `scaleX(${flipX})`,
      }}
    >
      {/* Trumpet body SVG */}
      <motion.div
        animate={{ rotate: [-2, 2, -2] }}
        transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut', delay }}
      >
        <svg width="110" height="60" viewBox="0 0 110 60">
          <defs>
            <linearGradient id={`tg${side}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f59e0b" />
              <stop offset="50%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
          </defs>
          {/* Bell */}
          <ellipse cx="18" cy="30" rx="18" ry="22" fill={`url(#tg${side})`} stroke="#92400e" strokeWidth="1.5" />
          {/* Tube */}
          <rect x="14" y="27" width="70" height="6" rx="3" fill={`url(#tg${side})`} stroke="#92400e" strokeWidth="1" />
          {/* Mouthpiece */}
          <rect x="82" y="28" width="20" height="4" rx="2" fill="#fbbf24" />
          <circle cx="104" cy="30" r="3" fill="#fbbf24" stroke="#d97706" strokeWidth="1" />
          {/* Valves */}
          {[38,50,62].map((x, i) => (
            <g key={i}>
              <rect x={x} y="22" width="7" height="16" rx="3" fill="#d97706" stroke="#92400e" strokeWidth="1" />
              <rect x={x+1} y="24" width="5" height="6" rx="2" fill="#fbbf24" />
            </g>
          ))}
          {/* Sound waves */}
          {[0,1,2].map(i => (
            <motion.path key={i}
              d={`M 0 ${22 + i*8} Q -12 ${26 + i*8} 0 ${30 + i*8}`}
              stroke="#fbbf24" strokeWidth="1.5" fill="none" opacity="0.6"
              animate={{ opacity: [0, 0.8, 0], x: [-5, -20] }}
              transition={{ duration: 0.8, repeat: Infinity, delay: delay + i * 0.15 }}
            />
          ))}
        </svg>
      </motion.div>
    </motion.div>
  );
};

// ── Victory sparks ──
const Sparks = () => (
  <>
    {[...Array(16)].map((_, i) => {
      const angle = (i / 16) * Math.PI * 2;
      const r = 80 + Math.random() * 120;
      return (
        <motion.div key={i}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
          animate={{ x: Math.cos(angle) * r, y: Math.sin(angle) * r, opacity: [0, 1, 0], scale: [0, 1.5, 0] }}
          transition={{ duration: 1.2, delay: 0.6 + Math.random() * 0.4 }}
          style={{ position: 'absolute', width: 6, height: 6, borderRadius: '50%', background: ['#ffd700','#fff','#fbbf24'][i % 3] }}
        />
      );
    })}
  </>
);

// ── Main VictoryScreen component ──
const VictoryScreen = ({ winner, isMe, winnerKingdom, onReturn }) => {
  const navigate = useNavigate();
  const flagColors = KINGDOM_FLAGS[winnerKingdom] || KINGDOM_FLAGS.Unknown;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {/* The field where it was decided */}
      <div style={{ position: 'absolute', inset: 0, filter: isMe ? 'none' : 'grayscale(0.85)' }}>
        <BattleField kingdom={winnerKingdom} dimmed />
      </div>

      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
        style={{ position: 'absolute', inset: 0, background: isMe ? 'radial-gradient(ellipse at center, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.82) 100%)' : 'radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.9) 100%)', backdropFilter: 'blur(3px)' }}
      />

      {/* Flags — left and right sides */}
      <WavingFlag colors={flagColors} side="left"  delay={0.2} defeated={!isMe} />
      <WavingFlag colors={flagColors} side="right" delay={0.35} defeated={!isMe} />

      {/* Trumpets — only for winner */}
      {isMe && (
        <>
          <Trumpet side="left"  delay={0.5} />
          <Trumpet side="right" delay={0.65} />
        </>
      )}

      {/* Light rays for winner */}
      {isMe && [...Array(10)].map((_, i) => (
        <motion.div key={i}
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{ opacity: [0, 0.25, 0.08], scaleY: 1 }}
          transition={{ duration: 1.8, delay: 0.3 + i * 0.06, ease: 'easeOut' }}
          style={{ position: 'absolute', top: 0, left: '50%', width: 2, height: '65vh', background: 'linear-gradient(to bottom, rgba(255,215,0,0.5), transparent)', transformOrigin: 'top center', transform: `rotate(${i * 36}deg)`, pointerEvents: 'none', zIndex: 201 }}
        />
      ))}

      {/* Falling gold particles for winner */}
      {isMe && [...Array(24)].map((_, i) => (
        <motion.div key={"f"+i}
          initial={{ y: '-5vh', opacity: 0 }}
          animate={{ y: '110vh', opacity: [0, 1, 1, 0] }}
          transition={{ duration: 3.5 + Math.random(), delay: 0.8 + Math.random(), ease: 'linear' }}
          style={{ position: 'absolute', left: `${(i / 24) * 100}%`, zIndex: 202, width: 5, height: 5, borderRadius: '50%', background: ['#ffd700','#fff','#fbbf24','#f59e0b'][i % 4], pointerEvents: 'none' }}
        />
      ))}

      {/* Central card */}
      <motion.div
        initial={{ scale: 0.1, opacity: 0, y: 80 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 160, damping: 18, delay: 0.2 }}
        style={{ position: 'relative', zIndex: 207, textAlign: 'center', maxWidth: 380, width: '86%' }}
      >
        {/* Sparks around card */}
        {isMe && (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 208 }}>
            <Sparks />
          </div>
        )}

        <div style={{
          background: isMe ? 'linear-gradient(160deg, rgba(14,10,4,0.99), rgba(20,14,4,0.99))' : 'linear-gradient(160deg, rgba(8,8,10,0.99), rgba(12,12,18,0.99))',
          borderRadius: 22,
          padding: 'clamp(28px,5vw,44px) clamp(24px,6vw,52px)',
          border: isMe ? '2px solid rgba(255,215,0,0.65)' : '2px solid rgba(200,200,220,0.2)',
          boxShadow: isMe ? '0 0 60px rgba(255,215,0,0.18), 0 32px 64px rgba(0,0,0,0.85)' : '0 32px 64px rgba(0,0,0,0.85)',
        }}>
          {/* Crown / White flag icon */}
          <div style={{ perspective: 700, marginBottom: 16 }}>
            <motion.div
              initial={{ scale: 0, rotateY: isMe ? -180 : 0, rotateX: 40 }}
              animate={isMe
                ? { scale: 1, rotateY: [-26, 26, -26], rotateX: [40, 14, 20, 14] }
                : { scale: 1, rotateY: 0, rotateX: 0 }}
              transition={isMe
                ? { scale: { type: 'spring', stiffness: 220, damping: 14, delay: 0.45 },
                    rotateY: { duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.9 },
                    rotateX: { duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.9 } }
                : { type: 'spring', stiffness: 220, damping: 14, delay: 0.45 }}
              style={{
                fontSize: isMe ? 72 : 52, lineHeight: 1, transformStyle: 'preserve-3d',
                filter: isMe ? 'drop-shadow(0 0 26px rgba(255,215,0,0.85))' : 'drop-shadow(0 0 8px rgba(200,200,200,0.3))',
              }}
            >
              {isMe ? '♛' : '🏳️'}
            </motion.div>
          </div>

          {/* Main title */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.25em', textTransform: 'uppercase', color: isMe ? 'rgba(255,215,0,0.5)' : 'rgba(200,200,220,0.35)', marginBottom: 8 }}>
              {isMe ? 'Vittoria' : 'Sconfitta'}
            </div>
            <h1 style={{
              fontFamily: "'Cinzel',serif",
              fontSize: 'clamp(22px,5vw,36px)', fontWeight: 900, margin: '0 0 6px',
              background: isMe ? 'linear-gradient(to bottom, #ffffff, #FFEC80 40%, #FFD700)' : 'linear-gradient(to bottom, #d1d5db, #9ca3af)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {isMe ? 'Hai Vinto' : 'Sei Stato Sconfitto'}
            </h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
            style={{ color: 'rgba(255,255,255,0.38)', fontSize: 13, margin: '0 0 6px', lineHeight: 1.6 }}>
            {isMe
              ? "La corona ti appartiene. Sei il sovrano assoluto."
              : (winner?.username + " ha conquistato il regno. Il tuo esercito si arrende.")}
          </motion.p>

          {/* Winner name (if not me) */}
          {!isMe && winner?.username && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
              style={{ fontSize: 18, fontWeight: 700, color: '#fbbf24', fontFamily: "'Cinzel',serif", marginBottom: 8 }}>
              {winner.username}
            </motion.div>
          )}

          {/* CTA */}
          <motion.button
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
            onClick={() => navigate('/games')}
            whileHover={{ scale: 1.04, boxShadow: '0 0 24px rgba(255,215,0,0.4)' }}
            whileTap={{ scale: 0.97 }}
            style={{ marginTop: 20, background: isMe ? 'linear-gradient(145deg, #fbbf24, #d97706)' : 'linear-gradient(145deg, #374151, #1f2937)', border: isMe ? '2px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '11px 32px', fontSize: 12, fontWeight: 900, color: isMe ? '#1c1917' : '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Cinzel',serif" }}
          >
            Torna ai Giochi
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VictoryScreen;
